import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { RotateCw, RotateCcw, ZoomIn, Crop, Check, X } from 'lucide-react';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e) => reject(e));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

async function getCroppedImg(imageSrc, cropPixels, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const rad = (rotation * Math.PI) / 180;

  // Canvas assez grand pour contenir l'image tournée
  const bBoxWidth = Math.abs(Math.cos(rad) * image.width) + Math.abs(Math.sin(rad) * image.height);
  const bBoxHeight = Math.abs(Math.sin(rad) * image.width) + Math.abs(Math.cos(rad) * image.height);
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const data = ctx.getImageData(0, 0, bBoxWidth, bBoxHeight);

  // Canvas final = zone rognée
  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  ctx.putImageData(data, -cropPixels.x, -cropPixels.y);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

const ASPECTS = [
  { label: 'Libre', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
];

export const PhotoEditor = ({ open, imageSrc, onClose, onSave, uploading = false }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
    if (blob) await onSave(blob);
  };

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setAspect(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="bg-white sm:max-w-2xl" data-testid="photo-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#8B7355]"><Crop className="h-5 w-5" />Modifier la photo</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-[360px] bg-slate-900 rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect || undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              restrictPosition={false}
            />
          )}
        </div>

        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-[#8B7355]" />
            <input type="range" min={1} max={4} step={0.05} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[#8B7355]" data-testid="photo-editor-zoom" />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} data-testid="photo-editor-rotate-left">
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="photo-editor-rotate-right">
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {ASPECTS.map((a) => (
                <button key={a.label} type="button"
                  onClick={() => setAspect(a.value)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${aspect === a.value ? 'bg-[#8B7355] text-white border-[#8B7355]' : 'bg-white border-[#8B7355]/40 text-[#1A1A1A] hover:border-[#8B7355]'}`}
                  data-testid={`photo-editor-aspect-${a.label}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={uploading}><X className="h-4 w-4 mr-1" />Annuler</Button>
          <Button onClick={handleSave} disabled={uploading || !croppedAreaPixels} className="bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="photo-editor-save">
            <Check className="h-4 w-4 mr-1" />{uploading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoEditor;
